import { useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';

/** Injected by `@vibesites/blocks` `perq-floor-plan`. */
const PERQ_SCRIPT_ID = 'perq-floor-plan-script';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  /**
   * Full SEO subtitle sentence. Use {{field_name}} placeholders for property
   * fields (e.g. {{property_name}}, {{property_address_city}}).
   */
  subtitle?: string;
  blurbField?: string;
  defaultText?: string;
  /**
   * When true, keep a PERQ / Fatwin bind target (`fw-cta-button-target`) in
   * the Floor Plans hero. The button is only *shown* once Fatwin paints its
   * live Floor Plan Match UI (the right-edge tab) — a script URL alone is
   * not enough.
   */
  showPerqCta?: boolean;
  /** Label for the PERQ hero CTA. Ignored unless the CTA is shown. */
  ctaLabel?: string;
  // Retained for pages.json compatibility with the parent theme.
  ctaUrl?: string;
}

const FIELD = (field: string) =>
  `<span data-type="field-token" data-field="${field}"></span>`;

/** Expands {{field}} placeholders into richtext field tokens and wraps in <p>. */
function buildSubtitleHtml(template: string): string {
  const expanded = template.replace(/\{\{(\w+)\}\}/g, (_, field: string) => FIELD(field));
  return `<p>${expanded}</p>`;
}

function isOurPerqBindTarget(el: Element): boolean {
  if (el.id === PERQ_SCRIPT_ID) return true;
  if (el instanceof HTMLScriptElement) return true;
  if (el.classList.contains('fw-cta-button-target')) return true;
  if (el.classList.contains('hero-solid-perq-cta')) return true;
  if (el.closest('.hero-solid-perq-cta-wrap')) return true;
  return false;
}

/**
 * True when Fatwin has painted live Floor Plan Match UI (the right-edge
 * tab on Fairmont), not merely when the embed script tag exists.
 */
function isFatwinFloorPlanActive(): boolean {
  if (typeof document === 'undefined') return false;

  const painted = document.querySelectorAll(
    [
      '[class*="fw-"]',
      '[id*="fw-"]',
      '[class*="fatwin"]',
      '[id*="fatwin"]',
      'iframe[src*="fatwin"]',
      'iframe[src*="perq"]',
    ].join(','),
  );
  for (const el of painted) {
    if (!isOurPerqBindTarget(el)) return true;
  }

  const labeled = document.body?.querySelectorAll('div, button, a, span, aside') ?? [];
  for (const el of labeled) {
    if (isOurPerqBindTarget(el)) continue;
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (text !== 'Floor Plan Match') continue;
    const style = window.getComputedStyle(el);
    if (style.position === 'fixed' || style.position === 'sticky') return true;
    const parent = el.parentElement;
    if (parent && window.getComputedStyle(parent).position === 'fixed') return true;
  }

  return false;
}

/** Watches for Fatwin's painted tab; false until that UI actually appears. */
function useFatwinFloorPlanActive(enabled: boolean): boolean {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setActive(false);
      return;
    }

    const check = () => {
      if (isFatwinFloorPlanActive()) {
        setActive(true);
        return true;
      }
      return false;
    };

    if (check()) return;

    const observer = new MutationObserver(() => {
      if (check()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    // Fatwin may paint into a shadow root MutationObserver cannot see.
    const poll = window.setInterval(() => {
      if (check()) window.clearInterval(poll);
    }, 500);
    return () => {
      observer.disconnect();
      window.clearInterval(poll);
    };
  }, [enabled]);

  return enabled && active;
}

/**
 * Aion Management override of the Belmont HeroSolidBG.
 *
 * Interior pages do not surface a navigational CTA in the hero — those live
 * in the sticky header. The one exception is the optional PERQ Floor Plan
 * Match button on Floor Plans (`showPerqCta`). Fatwin binds
 * `fw-cta-button-target`; we keep that node in the DOM so it can bind, and
 * only reveal the button once Fatwin paints its live tab.
 */
export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  subtitle,
  blurbField,
  defaultText,
  showPerqCta = false,
  ctaLabel = 'Find Your Perfect Floor Plan',
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const blurbPath = `${prefix}.blurb`;

  const subtitleDefault = buildSubtitleHtml(
    subtitle || 'Explore this page',
  );

  const perqActive = useFatwinFloorPlanActive(showPerqCta);

  return (
    <section
      className="bg-primary text-primary-foreground pt-6 md:pt-8 pb-8 md:pb-12"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 items-center pt-2 md:pt-2 lg:pt-6">

          {/* Page Title + SEO subtitle */}
          <div className="min-w-0 w-full flex flex-col gap-2 items-center">
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="font-heading text-[60px] leading-[90px] font-normal break-words text-center"
            />
            <RichTextContent
              path={subtitlePath}
              label="SEO Subtitle"
              defaultValue={subtitleDefault}
              as="h2"
              className="font-body text-sm sm:text-base md:text-lg font-normal tracking-wide leading-snug text-center max-w-3xl"
            />
          </div>

          {/* Blurb */}
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={defaultText || ''}
            as="div"
            className="font-body text-base md:text-lg leading-relaxed opacity-90 [&_a]:underline text-center"
          />

          {/* Bind target always mounted when opted in so Fatwin can attach;
              visually hidden until the live Floor Plan Match tab exists. */}
          {showPerqCta && (
            <div
              className="hero-solid-perq-cta-wrap text-center"
              data-perq-ready={perqActive ? 'true' : 'false'}
              aria-hidden={perqActive ? undefined : true}
            >
              <button
                type="button"
                className="fw-cta fw-cta-button-target hero-solid-perq-cta group/cta relative inline-flex items-center justify-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{ctaLabel}</span>
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />

      {showPerqCta && (
        <style>{`
          .hero-solid-perq-cta-wrap[data-perq-ready="false"] {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
            pointer-events: none;
          }
          /* Utility-bar gray (Header .header-util-row / --vibe-color-secondary). */
          .hero-solid-perq-cta {
            background: var(--vibe-color-secondary);
            border: 0;
            border-radius: 5px;
            color: var(--vibe-color-text-on-secondary, #ffffff);
            box-shadow: none;
            cursor: pointer;
            transition: background-color 0.25s ease, color 0.25s ease,
              box-shadow 0.25s ease, transform 0.25s ease;
          }
          .hero-solid-perq-cta::before {
            content: '';
            position: absolute;
            inset: 0;
            background: transparent;
            opacity: 0;
            transition: opacity 0.25s ease;
          }
          .hero-solid-perq-cta:hover {
            background: color-mix(in srgb, var(--vibe-color-secondary) 55%, white);
            color: var(--vibe-color-text-on-secondary, #ffffff);
            box-shadow:
              0 2px 6px rgba(0,0,0,0.1),
              0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
            transform: translateY(-1px);
          }
          .hero-solid-perq-cta:hover::before {
            opacity: 0;
          }
          .hero-solid-perq-cta:active {
            transform: translateY(0);
            box-shadow: none;
          }
        `}</style>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.subtitle': {
      label: 'SEO Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Explore this page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
